import type { ForecastProvider, WeekMatrix } from './types';
import { genWeek } from '../components/Campaign/CoverageHeatmap';
import { proceduralProvider } from './proceduralProvider';
import { hourlyClient } from '../../../lib/hourly-orpc-client';

type ScheduleData = Awaited<ReturnType<typeof hourlyClient.schedule.getSchedule>>;

/**
 * Extracts a 7×48 demand grid from a schedule's labor forecasts for the given
 * job role family. Returns null if no matching family or no forecasts exist.
 */
export function extractDemandGrid(
  schedule: ScheduleData,
  jobRoleFamilyId: string,
): number[][] | null {
  const families = (schedule as unknown as {
    locationJobRoleFamilies?: Array<{
      jobRoleFamilyId: string;
      laborForecasts?: Array<{ timestamp: string; value: number }>;
    }>;
  }).locationJobRoleFamilies;

  if (!families) return null;

  const family = families.find((f) => f.jobRoleFamilyId === jobRoleFamilyId);
  if (!family || !family.laborForecasts || family.laborForecasts.length === 0) return null;

  const grid: number[][] = Array.from({ length: 7 }, () => new Array<number>(48).fill(0));
  const weekStartMs = new Date(schedule.startDate).getTime();

  for (const forecast of family.laborForecasts) {
    const ts = new Date(forecast.timestamp);
    const dayIndex = Math.floor((ts.getTime() - weekStartMs) / (24 * 60 * 60 * 1000));
    if (dayIndex < 0 || dayIndex >= 7) continue;

    const slotIndex = Math.floor((ts.getUTCHours() * 60 + ts.getUTCMinutes()) / 30);
    if (slotIndex < 0 || slotIndex >= 48) continue;

    grid[dayIndex][slotIndex] = Math.round(forecast.value);
  }

  return grid;
}

/**
 * Builds a WeekMatrix by combining real demand from the schedule API with
 * scaled procedural supply data.
 */
export function buildHybridMatrix(
  schedule: ScheduleData,
  jobRoleFamilyId: string,
  role: string,
  weekOffset: number,
): WeekMatrix {
  const demandGrid = extractDemandGrid(schedule, jobRoleFamilyId);

  if (demandGrid === null) {
    return proceduralProvider.getWeekMatrix(role, weekOffset, false);
  }

  // Get procedural data for supply shape
  const proceduralMatrix = genWeek(role, weekOffset, false);

  // Compute average non-zero demand from real data
  const realDemandValues = demandGrid.flat().filter((v) => v > 0);
  const avgRealDemand =
    realDemandValues.length > 0
      ? realDemandValues.reduce((sum, v) => sum + v, 0) / realDemandValues.length
      : 0;

  // Compute average non-zero procedural demand
  const proceduralDemandValues = proceduralMatrix
    .flat()
    .filter((slot) => !slot.closed && slot.demand > 0)
    .map((slot) => slot.demand);
  const avgProceduralDemand =
    proceduralDemandValues.length > 0
      ? proceduralDemandValues.reduce((sum, v) => sum + v, 0) / proceduralDemandValues.length
      : 0;

  const scaleFactor =
    avgProceduralDemand > 0 ? avgRealDemand / avgProceduralDemand : 1;

  return proceduralMatrix.map((daySlots, dayIndex) =>
    daySlots.map((slot, slotIndex) => {
      if (slot.closed) {
        return { demand: 0, supply: 0, closed: true };
      }
      const realDemand = demandGrid[dayIndex][slotIndex];
      const scaledSupply = Math.round(slot.supply * scaleFactor);
      return {
        demand: realDemand,
        supply: scaledSupply,
        closed: false,
      };
    }),
  );
}

/**
 * Creates a ForecastProvider that uses real schedule data for demand and
 * scaled procedural data for supply.
 *
 * @param schedulesByWeek - Map from weekOffset to schedule data (or null if
 *   the fetch failed / returned nothing)
 * @param jobRoleFamilyId - The Hourly job role family ID to extract forecasts for
 * @param fallbackRole - Procedural role name used when no schedule is available
 */
export function buildHybridProvider(
  schedulesByWeek: Map<number, ScheduleData | null>,
  jobRoleFamilyId: string,
  fallbackRole: string,
): ForecastProvider {
  return {
    getWeekMatrix(role, weekOffset, withCampaign, overlayForRole): WeekMatrix {
      const schedule = schedulesByWeek.get(weekOffset);

      if (schedule != null) {
        // Campaign overlay is handled upstream in CenterVisuals; delegate to
        // procedural when withCampaign is true so campaign simulation still works.
        if (withCampaign) {
          return proceduralProvider.getWeekMatrix(role, weekOffset, withCampaign, overlayForRole);
        }
        return buildHybridMatrix(schedule, jobRoleFamilyId, role, weekOffset);
      }

      return proceduralProvider.getWeekMatrix(role, weekOffset, withCampaign, overlayForRole);
    },

    isLive(weekOffset: number): boolean {
      return schedulesByWeek.has(weekOffset) && schedulesByWeek.get(weekOffset) !== null;
    },
  };
}
